/**
 * PostActivation Analytics Service
 * 
 * Tracks talent behavior around post-activation content creation:
 * - Copy Caption clicks
 * - Copy Hashtags clicks
 * - Share Now clicks
 * - Post confirmation (Yes/Not yet)
 * - Time from clock-out to post
 * 
 * Data is sent to backend for analytics and stored locally for UX
 */

import api from './api';

const STORAGE_KEY = 'ievntur_post_analytics';

// Event types
export const POST_EVENTS = {
  COPY_CAPTION: 'copy_caption',
  COPY_HASHTAGS: 'copy_hashtags',
  COPY_ALL_HASHTAGS: 'copy_all_hashtags',
  COPY_BRAND_TAG: 'copy_brand_tag',
  SHARE_NOW_CLICKED: 'share_now_clicked',
  POST_CONFIRMED_YES: 'post_confirmed_yes',
  POST_CONFIRMED_NO: 'post_confirmed_no',
  POST_KIT_OPENED: 'post_kit_opened',
  QUICK_ACTION_USED: 'quick_action_used',
  TIMING_INDICATOR_VIEWED: 'timing_indicator_viewed'
};

// Get stored analytics
const getStoredAnalytics = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { events: [], sessions: {} };
  } catch {
    return { events: [], sessions: {} };
  }
};

// Save analytics locally
const saveAnalytics = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save analytics:', e);
  }
};

// Track an event
export const trackPostEvent = async (eventType, metadata = {}) => {
  const timestamp = new Date().toISOString();
  const event = {
    event_type: eventType,
    timestamp,
    metadata: {
      ...metadata,
      user_agent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  };
  
  // Store locally
  const analytics = getStoredAnalytics();
  analytics.events.push(event);
  
  // Keep only last 100 events locally
  if (analytics.events.length > 100) {
    analytics.events = analytics.events.slice(-100);
  }
  
  saveAnalytics(analytics);
  
  // Send to backend (fire and forget)
  try {
    await api.post('/analytics/post-activation', event);
  } catch (e) {
    console.warn('Failed to send analytics:', e);
  }
  
  return event;
};

// Track post-event session (from clock-out to post confirmation)
export const startPostSession = (bookingId, venueName) => {
  const analytics = getStoredAnalytics();
  const sessionId = `session_${Date.now()}`;
  
  analytics.sessions[sessionId] = {
    booking_id: bookingId,
    venue_name: venueName,
    started_at: new Date().toISOString(),
    events: [],
    posted: null,
    post_time: null
  };
  
  saveAnalytics(analytics);
  
  // Store current session ID
  localStorage.setItem('ievntur_current_post_session', sessionId);
  
  return sessionId;
};

// Mark session as posted
export const markSessionPosted = (posted = true) => {
  const sessionId = localStorage.getItem('ievntur_current_post_session');
  if (!sessionId) return;
  
  const analytics = getStoredAnalytics();
  if (analytics.sessions[sessionId]) {
    analytics.sessions[sessionId].posted = posted;
    analytics.sessions[sessionId].post_time = new Date().toISOString();
    
    // Calculate time from session start to post
    const startTime = new Date(analytics.sessions[sessionId].started_at);
    const postTime = new Date();
    const minutesToPost = Math.round((postTime - startTime) / (1000 * 60));
    analytics.sessions[sessionId].minutes_to_post = minutesToPost;
    
    saveAnalytics(analytics);
    
    // Track the confirmation event
    trackPostEvent(
      posted ? POST_EVENTS.POST_CONFIRMED_YES : POST_EVENTS.POST_CONFIRMED_NO,
      { session_id: sessionId, minutes_to_post: minutesToPost }
    );
  }
};

// Get analytics summary
export const getAnalyticsSummary = () => {
  const analytics = getStoredAnalytics();
  
  const eventCounts = {};
  analytics.events.forEach(e => {
    eventCounts[e.event_type] = (eventCounts[e.event_type] || 0) + 1;
  });
  
  const sessions = Object.values(analytics.sessions);
  const postedSessions = sessions.filter(s => s.posted === true);
  const notPostedSessions = sessions.filter(s => s.posted === false);
  
  const avgMinutesToPost = postedSessions.length > 0
    ? Math.round(postedSessions.reduce((sum, s) => sum + (s.minutes_to_post || 0), 0) / postedSessions.length)
    : null;
  
  return {
    total_events: analytics.events.length,
    event_counts: eventCounts,
    total_sessions: sessions.length,
    posted_count: postedSessions.length,
    not_posted_count: notPostedSessions.length,
    post_rate: sessions.length > 0 ? Math.round((postedSessions.length / sessions.length) * 100) : 0,
    avg_minutes_to_post: avgMinutesToPost,
    copy_caption_clicks: eventCounts[POST_EVENTS.COPY_CAPTION] || 0,
    copy_hashtags_clicks: eventCounts[POST_EVENTS.COPY_HASHTAGS] || 0,
    share_now_clicks: eventCounts[POST_EVENTS.SHARE_NOW_CLICKED] || 0
  };
};

// Check if user has pending post session (for showing confirmation prompt)
export const hasPendingPostSession = () => {
  const sessionId = localStorage.getItem('ievntur_current_post_session');
  if (!sessionId) return false;
  
  const analytics = getStoredAnalytics();
  const session = analytics.sessions[sessionId];
  
  // Session exists and hasn't been confirmed yet
  return session && session.posted === null;
};

// Clear current session (after confirmation)
export const clearCurrentSession = () => {
  localStorage.removeItem('ievntur_current_post_session');
};

export default {
  trackPostEvent,
  startPostSession,
  markSessionPosted,
  getAnalyticsSummary,
  hasPendingPostSession,
  clearCurrentSession,
  POST_EVENTS
};
